const pluginName = 'subEventsPlugin';
const defaults = {
  eventId: null,
  loadRegistrantFeatures: true,
  selectOnlySubEvent: true,
  routes: {
    subEvents: null,
    subEventSelection: null,
    clearSubEventSelection: null,
  },
  callbacks: {
    toggleRegistrantSpinner: () => {},
    // when the user clicks a sub-event
    subEventClicked: () => {},
    // when the server responds to let us know the sub-event click was good
    subEventSelected: () => {},
    // only relevant in series - when the user tries to clear their sub-event selection for an event
    subEventClearAttempted: () => {},
    // only relevant in series - when the user successfully clears their sub-event selection for an event
    subEventCleared: () => {},
    loadRegistrantFeatures: () => {},
    handleErrorResponse: () => {},
    unloadRegistrationComponents: () => {},
  },
};
const selectors = {
  subEventSpinner: '.js-registration-sub-event-spinner',
  subEventList: '.js-sub-event-list',
  subEventRadio: '.js-sub-event-radio',
  subEventSelect: '.js-sub-event-select',
  subEventFullParticipantAlert: '.js-sub-event-full-participant-alert',
  subEventGroup: '.sub-event-group',
  subEventGroupToggle: '.sub-event-group__toggle',
  subEventGroupCollapse: '.sub-event-group__collapse',
  subEventClear: '[data-js-clear-sub-event]',
  kioskSubEventSelectorBlock: '[data-js-kiosk-sub-event-selector-block]',
};
const classes = {
  hidden: 'hidden',
  in: 'in',
};

// TODO: remove when dropping strava support
const stravaElements = {};

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

/**
 * If there is a pre-selected sub-event (e.g. when a user goes back a step), trigger the load function
 * @param data
 */
const loadPreselectedSubEvent = (data) => {
  const $subEventSelect = data.elements.$element.find(selectors.subEventSelect);
  const $checkedSubEvent = data.elements.$element.find(selectors.subEventRadio).filter(':checked');

  if ($subEventSelect.val()) {
    $subEventSelect.trigger('change');

    return true;
  }

  if ($checkedSubEvent.length) {
    $checkedSubEvent.trigger('click');

    return true;
  }

  return false;
};

/**
 * If there's only 1 available sub-event, be polite and select it
 * @param data
 * @return {boolean}
 */
const maybeSelectOnlySubEvent = (data) => {
  if (!data.settings.selectOnlySubEvent) {
    return false;
  }

  const $checkableRadioSubEvents = data.elements.$element.find(selectors.subEventRadio).filter(':enabled');
  const $selectableSelectSubEvents = data.elements.$element.find(selectors.subEventSelect).find(':enabled');

  if ($checkableRadioSubEvents.length === 1) {
    $checkableRadioSubEvents.prop('checked', true).trigger('click');

    return true;
  }

  if ($selectableSelectSubEvents.length === 1) {
    $selectableSelectSubEvents
      .closest('select')
      .val($selectableSelectSubEvents.attr('value'))
      .trigger('change');

    return true;
  }

  return false;
};

/**
 * Toggle the sub-event spinner for the registrant
 * @param data
 * @param isVisible
 */
const toggleSubEventsSpinner = (data, isVisible) => {
  data.elements.$subEventSpinner.toggleClass(classes.hidden, !isVisible);
  data.elements.$subEventList.toggleClass(classes.hidden, isVisible);
};

/**
 * Make sure that the parent group of a sub-event is expanded if it's been selected (useful for pre-selection)
 * @param data
 * @param $subEvent
 */
const synchronizeSubEventGroups = (data, $subEvent) => {
  const $subEventGroup = $subEvent.closest(selectors.subEventGroup);

  if (!$subEventGroup.length) {
    return;
  }

  const $subEventGroupToggle = $subEventGroup.find(selectors.subEventGroupToggle);
  const $subEventGroupCollapse = $subEventGroup.find(selectors.subEventGroupCollapse);

  $subEventGroupToggle.removeClass('collapsed');
  $subEventGroupToggle.attr('aria-expanded', true);

  $subEventGroupCollapse.addClass(classes.in);
  $subEventGroupCollapse.attr('aria-expanded', true);
};

/**
 * Display the applicable Strava integration information
 * @todo remove when dropping strava support
 * @param data
 * @param isVisible
 * @param $subEvent
 */
const displayStravaIntegration = (data, isVisible, $subEvent = null) => {
  if (!isVisible || ($subEvent && !$subEvent[0].hasAttribute('data-strava'))) {
    stravaElements.$integration.addClass(classes.hidden);
    return;
  }

  stravaElements.$integration.removeClass(classes.hidden);

  // If not authenticated, hide the spinner and wait for user interaction
  if (stravaElements.$integration.data('strava') === false) {
    triggerHook(data, 'toggleRegistrantSpinner', false);
    return;
  }

  const subEventValue = $subEvent.val();
  const $warning = stravaElements.$warnings.filter(`[data-strava-warning="${subEventValue}"]`).first();

  // If there is a warning (already registered) display it and wait for user interaction
  if ($warning.length) {
    triggerHook(data, 'toggleRegistrantSpinner', false);
    $warning.removeClass(classes.hidden);
    stravaElements.$authenticated.addClass(classes.hidden);
    return;
  }

  // Let's also check that the user has given us the appropriate permissions
  if (stravaElements.$noPermission.length) {
    triggerHook(data, 'toggleRegistrantSpinner', false);
    stravaElements.$noPermission.removeClass(classes.hidden);
    stravaElements.$authenticated.addClass(classes.hidden);
    stravaElements.$warnings.addClass(classes.hidden);
    return;
  }

  // Otherwise, display the authenticated info box
  stravaElements.$authenticated.removeClass(classes.hidden);
  stravaElements.$warnings.addClass(classes.hidden);

  // Also, we can load the features
  triggerHook(data, 'loadRegistrantFeatures', $subEvent, parseInt(subEventValue, 10));
};

/**
 * Maybe load the sub-event features if not blocked by another integration
 * @param data
 * @param $subEvent
 * @param subEventId
 */
const maybeLoadRegistrantFeatures = (data, $subEvent, subEventId) => {
  if (!data.settings.loadRegistrantFeatures) {
    return;
  }

  // TODO: remove when dropping strava support
  // The selected sub-event might have Strava integration
  if ($subEvent[0].hasAttribute('data-strava')) {
    displayStravaIntegration(data, true, $subEvent);
    return;
  }

  // Otherwise, load as usual
  triggerHook(data, 'loadRegistrantFeatures', $subEvent, subEventId);
};

/**
 * Reload the participant sub-event select options
 * @param data
 * @param autofocus
 */
const reloadRegistrantSubEventSelector = (data, autofocus = false) => {
  const $subEvents = data.elements.$element.find(selectors.subEventList);
  const $subEventFullParticipantAlert = data.elements.$element.find(selectors.subEventFullParticipantAlert);

  toggleSubEventsSpinner(data, true);
  $subEventFullParticipantAlert.addClass(classes.hidden);

  if (data.requests.subEventSelector) {
    data.requests.subEventSelector.abort();
  }

  /* eslint-disable-next-line no-param-reassign */
  data.requests.subEventSelector = $.ajax({
    type: 'get',
    url: data.settings.routes.subEvents,
  })
    .done((response) => {
      if (response.data.isOpen) {
        const subEventPromise = $subEvents.html(response.data.html).promise();

        if (autofocus) {
          subEventPromise.then(() => {
            $subEvents
              .find(selectors.subEventSelect)
              .add(selectors.subEventRadio)
              .filter(':enabled')
              .first()
              .focus();
          });
        }

        subEventPromise.then(() => maybeSelectOnlySubEvent(data));
        return;
      }

      if (data.settings.registrantIndex === 1) {
        window.location = data.settings.entryUrl;
        return;
      }

      $subEvents.addClass(classes.hidden);
      $subEventFullParticipantAlert.removeClass(classes.hidden);
    })
    .fail((response) => {
      triggerHook(data, 'handleErrorResponse', response);
    })
    .always(() => {
      /* eslint-disable-next-line no-param-reassign */
      data.requests.subEventSelector = null;
      toggleSubEventsSpinner(data, false);
    });
};

/**
 * Tell the server that we wanna clear the sub-event for the event
 * @param data
 */
const clearSubEvent = (data) => {
  if (data.requests.subEventClear) {
    data.requests.subEventClear.abort();
  }

  triggerHook(data, 'subEventClearAttempted');

  /* eslint-disable-next-line no-param-reassign */
  data.requests.subEventClear = $.ajax({
    type: 'post',
    url: data.settings.routes.clearSubEventSelection,
  }).always(() => {
    /* eslint-disable-next-line no-param-reassign */
    data.requests.subEventClear = null;
  }).done(() => {
    triggerHook(data, 'subEventCleared');
  }).fail((response) => {
    if (response.statusText === 'abort') {
      return;
    }

    triggerHook(data, 'handleErrorResponse', response);
    reloadRegistrantSubEventSelector(data);

    if (data.settings.loadRegistrantFeatures) {
      triggerHook(data, 'toggleRegistrantSpinner', false);
    }
  });
};

/**
 * If we have a clear button and radio buttons, show or hide the clear button based on whether we have any selected
 * radio buttons or, if no radios, a value in the select input
 * @param data
 */
const toggleClearSubEventButton = (data) => {
  const $clearSubEventButton = data.elements.$element.find(selectors.subEventClear);

  if (!$clearSubEventButton.length) {
    return;
  }

  const $radios = data.elements.$element.find(selectors.subEventRadio);

  if (!$radios.length) {
    $clearSubEventButton.toggleClass(
      classes.hidden,
      !data.elements.$element.find(selectors.subEventSelect).val(),
    );
    return;
  }

  $clearSubEventButton.toggleClass(classes.hidden, !$radios.filter(':checked').length);
};

/**
 * Select sub-event
 * @param data
 * @param event
 */
const selectSubEvent = (data, event) => {
  const $subEvent = $(event.currentTarget);
  const value = $subEvent.val();
  const subEventId = parseInt(value, 10);
  const subEventName = $subEvent.is(':radio')
    ? $subEvent.attr('data-sub-event-name')
    : $subEvent.find(':selected').attr('data-sub-event-name');
  const subEventPrice = $subEvent.is(':radio')
    ? $subEvent.attr('data-sub-event-price')
    : $subEvent.find(':selected').attr('data-sub-event-price');

  toggleClearSubEventButton(data);

  if (!subEventId) {
    clearSubEvent(data);
    return;
  }

  triggerHook(data, 'subEventClicked', $subEvent, subEventId);

  if (data.requests.subEvent) {
    data.requests.subEvent.abort();
  }

  if (data.settings.loadRegistrantFeatures) {
    triggerHook(data, 'unloadRegistrationComponents', !!value);
  }

  synchronizeSubEventGroups(data, $subEvent);

  // Hide the Strava integration
  // TODO: remove when dropping strava support
  stravaElements.$integration.addClass(classes.hidden);

  if (!value || !subEventId) {
    return;
  }

  // Lock in sub-event
  /* eslint-disable-next-line no-param-reassign */
  data.requests.subEvent = $.ajax({
    type: 'post',
    url: data.settings.routes.subEventSelection,
    data: {
      sub_event_id: subEventId,
    },
  }).always(() => {
    /* eslint-disable-next-line no-param-reassign */
    data.requests.subEvent = null;
  }).done(() => {
    triggerHook(data, 'subEventSelected', $subEvent, subEventId, subEventName, subEventPrice);
    maybeLoadRegistrantFeatures(data, $subEvent, subEventId);
  }).fail((response) => {
    if (response.statusText === 'abort') {
      return;
    }

    triggerHook(data, 'handleErrorResponse', response);
    reloadRegistrantSubEventSelector(data);

    if (data.settings.loadRegistrantFeatures) {
      triggerHook(data, 'toggleRegistrantSpinner', false);
    }
  });
};

/**
 * A wrapper function for when a sub-event has been selected via radio button. It has been necessitated by the
 * existence of sub-event groups.
 *
 * Due to the fact that our list of radio buttons can be segmented into groups which can each be shown or hidden,
 * keyboard navigation between items is broken, as radio buttons all belong to the same focusable item in the
 * focus order, regardless of how many other types of focusable items are shoved between them (e.g. buttons, which
 * we use to collapse/expand the groups).
 *
 * E.g.:
 *
 * [+ My first group] (focus order: 0)
 *   (.) My first sub-event (focus order: 1)
 *   ( ) My second sub-event
 * [+ My second group] (focus order: 2)
 *   ( ) My third sub-event (can't be focused as it's part of the first group!)
 *   ( ) My fourth sub-event
 *
 * That means if a user navigates to the toggle for the second group, they would expect that the next tab would
 * bring them into the second group of radio buttons, but that isn't the case. Also, if the user continues to hit
 * the down arrow beyond the end of the first group, the keyboard will continue reading items in the subsequent
 * groups without any indication that they have left the original group. Both of these issues completely break the
 * experience for users depending on assistive technology.
 *
 * To solve this, we have appended the group id to the name attributes of each sub-event, so that they are all
 * grouped separately and can be focused into in the proper order. However, we still need to pretend that all these
 * sub-groups are still a part of the same overall radio group, so we use JS to unselect all selected radio buttons
 * that aren't the latest one that was selected. We also automatically removed the required attribute on all radio
 * buttons after the first selection, as once a radio button has been selected, there will always be a radio button
 * selected.
 *
 * This is pretty hacky, but likely the only way to handle this without a big change in design.
 *
 * @param data
 * @param event
 */
const selectSubEventRadio = (data, event) => {
  const $selectedSubEvent = $(event.currentTarget);
  const value = $selectedSubEvent.val();

  if (!value) {
    return;
  }

  const $subEvents = data.elements.$element.find(selectors.subEventRadio);

  // if there is more than 1 group, the groups that don't have a selection will scream, so we're removing
  // all required attributes immediately after the first selection, as it's impossible to go back to a state
  // where no radio buttons are selected anyway
  $subEvents.prop('required', false);

  // uncheck all radio buttons except the one we have already
  $subEvents
    .not($selectedSubEvent)
    .prop('checked', false);

  // now onto the original sub-event selection logic
  selectSubEvent(data, event);
  toggleClearSubEventButton(data);
};

/**
 * Select a sub-event for onsite registration
 * @param data
 * @param event
 */
const selectOnSiteSubEvent = (data, event) => {
  const $selectedSubEvent = $(event.currentTarget);
  const $otherSubEvents = $selectedSubEvent.siblings(selectors.kioskSubEventSelectorBlock);

  $selectedSubEvent
    .addClass('kiosk-sub-event-selector-block--active')
    .attr('aria-pressed', 'true');

  $otherSubEvents
    .removeClass('kiosk-sub-event-selector-block--active')
    .attr('aria-pressed', 'false');

  selectSubEvent(data, event);
};

/**
 * Clear the sub-event radio
 * @param data
 */
const clearSubEventInput = (data) => {
  data.elements.$element
    .find(selectors.subEventSelect)
    .val('');
  data.elements.$element
    .find(selectors.subEventRadio)
    .filter(':checked')
    .prop('checked', false);

  clearSubEvent(data);
  toggleClearSubEventButton(data);
};

/**
 * Display the connect button and hide the logout copy
 * @param data
 * @param event
 */
const stravaLogout = (data, event) => {
  const $container = $(event.currentTarget).parent().parent();

  if (stravaElements.$connect.length) {
    $container.addClass(classes.hidden);
    stravaElements.$connect.removeClass(classes.hidden);
    triggerHook(data, 'unloadRegistrationComponents', false);
  }
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = (data) => {
  if (data.requests.subEventSelector) {
    data.requests.subEventSelector.abort();
  }

  if (data.requests.subEvent) {
    data.requests.subEvent.abort();
  }

  if (data.requests.subEventClear) {
    data.requests.subEventClear.abort();
  }
};

/**
 * Returns the selected sub-event id if one has been selected, otherwise returns false
 * @param data
 * @returns {boolean|number}
 */
const getSelectedSubEventId = (data) => {
  const $subEventSelect = data.elements.$element.find(selectors.subEventSelect);

  if ($subEventSelect.val()) {
    return parseInt($subEventSelect.val(), 10);
  }

  const $checkedSubEvent = data.elements.$element.find(selectors.subEventRadio).filter(':checked');

  if ($checkedSubEvent.length) {
    return parseInt($checkedSubEvent.val(), 10);
  }

  return false;
};

/**
 * Returns the event id for the sub-event plugin
 * @param data
 */
const getEventId = (data) => data.settings.eventId;

const methods = {
  reloadRegistrantSubEventSelector,
  cancelActiveRequests,
};

const getMethods = {
  getSelectedSubEventId,
  getEventId,
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const elements = {
    $element,
    $subEventSpinner: $element.find(selectors.subEventSpinner),
    $subEventList: $element.find(selectors.subEventList),
  };
  const requests = {
    subEvent: null,
    subEventSelector: null,
    subEventClear: null,
  };
  const data = {
    settings,
    elements,
    requests,
  };

  data.elements.$element.data(pluginName, data);

  toggleSubEventsSpinner(data, false);

  data.elements.$element.on('click', selectors.subEventRadio, (event) => selectSubEventRadio(data, event));
  data.elements.$element
    .on('click', selectors.kioskSubEventSelectorBlock, (event) => selectOnSiteSubEvent(data, event));
  data.elements.$element.on('click', selectors.subEventClear, () => clearSubEventInput(data));
  data.elements.$element.on('change', selectors.subEventSelect, (event) => selectSubEvent(data, event));

  // TODO: remove when dropping strava support
  stravaElements.$logout.on('click', (event) => stravaLogout(data, event));

  // Attempt to pre-select and load the sub-event
  // We only want to do the sub-event selection once
  if (!loadPreselectedSubEvent(data)) {
    maybeSelectOnlySubEvent(data);
  }
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });

  // TODO: remove when dropping strava support
  stravaElements.$integration = $('[data-js="strava-container"]');
  stravaElements.$authenticated = $('[data-js="strava-authenticated"]');
  stravaElements.$noPermission = $('[data-js="strava-no-permission"]');
  stravaElements.$warnings = $('[data-js="strava-warning"]');
  stravaElements.$logout = $('[data-js="strava-logout"]');
  stravaElements.$connect = $('[data-js="strava-connect"]');
});
